// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	createVoiceSubscriptionSnapshot,
	transitionVoiceSubscriptionSnapshot,
	type VideoQualityLevel,
	type VoiceRemoteTrackSubscriptionController,
	type VoiceSubscriptionCommand,
	type VoiceSubscriptionEvent,
	type VoiceSubscriptionSnapshot,
} from '@app/features/voice/engine/VoiceSubscriptionStateMachine';
import {asVoiceTrackSource, VoiceTrackSource} from '@app/features/voice/engine/VoiceTrackSource';
import type {RemoteParticipant, RemoteTrackPublication, Room} from 'livekit-client';
import {VideoQuality} from 'livekit-client';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('VideoSubscriptionManager');

const qualityMap: Record<VideoQualityLevel, VideoQuality> = {
	low: VideoQuality.LOW,
	medium: VideoQuality.MEDIUM,
	high: VideoQuality.HIGH,
};

export class VideoSubscriptionManager {
	private room: Room | null = null;
	private nativeController: VoiceRemoteTrackSubscriptionController | null = null;
	private snapshot: VoiceSubscriptionSnapshot = createVoiceSubscriptionSnapshot();
	private observers = new Map<string, IntersectionObserver>();
	private readonly intersectionOptions: IntersectionObserverInit = {
		root: null,
		rootMargin: '50px',
		threshold: [0, 0.1],
	};

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	setRoom(room: Room | null): void {
		this.room = room;
	}

	setNativeController(controller: VoiceRemoteTrackSubscriptionController | null): void {
		this.nativeController = controller;
	}

	cleanup(): void {
		this.transition({type: 'subscription.cleanup'});
	}

	subscribe(participantIdentity: string, element: HTMLElement | null, initialQuality: VideoQualityLevel = 'low'): void {
		if (!this.room && !this.nativeController) {
			logger.warn('No room available');
			return;
		}
		const participant = this.room?.remoteParticipants.get(participantIdentity) ?? null;
		if (!participant) {
			if (!this.nativeController) {
				logger.warn('Participant not found', {participantIdentity});
				return;
			}
		}
		logger.info('Subscribing to video', {participantIdentity, quality: initialQuality});
		const cameraPublication = this.findCameraPublication(participant);
		if (!cameraPublication && !this.nativeController) {
			logger.debug('No camera publication found', {participantIdentity});
		}
		const existingState = this.snapshot.context.entries.get(participantIdentity);
		this.transition({
			type: 'subscription.subscribe',
			participantIdentity,
			hasPublication: this.nativeController != null || cameraPublication != null,
			observedElement: element,
			quality: existingState ? undefined : initialQuality,
		});
		logger.debug('Video subscribe intent recorded', {participantIdentity});
	}

	unsubscribe(participantIdentity: string): void {
		if (!this.snapshot.context.entries.has(participantIdentity)) {
			logger.debug('Not subscribed', {participantIdentity});
			return;
		}
		logger.info('Unsubscribing from video', {participantIdentity});
		this.transition({type: 'subscription.unsubscribe', participantIdentity});
		logger.info('Video unsubscribed successfully', {participantIdentity});
	}

	setEnabled(participantIdentity: string, enabled: boolean): void {
		const state = this.snapshot.context.entries.get(participantIdentity);
		if (!state) {
			logger.debug('Not subscribed', {participantIdentity});
			return;
		}
		if (state.enabled === enabled) {
			return;
		}
		logger.debug('Setting video enabled state', {participantIdentity, enabled});
		this.transition({
			type: 'subscription.setEnabled',
			participantIdentity,
			hasPublication: this.hasCameraPublicationForIdentity(participantIdentity),
			enabled,
		});
	}

	setQuality(participantIdentity: string, quality: VideoQualityLevel): void {
		const state = this.snapshot.context.entries.get(participantIdentity);
		if (!state) {
			logger.debug('Not subscribed', {participantIdentity});
			return;
		}
		if (state.quality === quality) {
			return;
		}
		logger.debug('Setting video quality', {participantIdentity, quality});
		this.transition({
			type: 'subscription.setQuality',
			participantIdentity,
			hasPublication: this.hasCameraPublicationForIdentity(participantIdentity),
			quality,
		});
	}

	isSubscribed(participantIdentity: string): boolean {
		return this.snapshot.context.entries.get(participantIdentity)?.subscribed ?? false;
	}

	getQuality(participantIdentity: string): VideoQualityLevel | null {
		return this.snapshot.context.entries.get(participantIdentity)?.quality ?? null;
	}

	reattachAfterPublish(participantIdentity: string): void {
		const state = this.snapshot.context.entries.get(participantIdentity);
		if (!state?.subscribed || (!this.room && !this.nativeController)) return;
		this.transition({
			type: 'subscription.reattachAfterPublish',
			participantIdentity,
			hasPublication: this.hasCameraPublicationForIdentity(participantIdentity),
		});
	}

	private findCameraPublication(participant: RemoteParticipant | null | undefined): RemoteTrackPublication | undefined {
		if (!participant) return undefined;
		for (const pub of participant.videoTrackPublications.values()) {
			if (asVoiceTrackSource(pub.source) === VoiceTrackSource.Camera) {
				return pub;
			}
		}
		return undefined;
	}

	private findCameraPublicationForIdentity(participantIdentity: string): RemoteTrackPublication | undefined {
		return this.findCameraPublication(this.room?.remoteParticipants.get(participantIdentity) ?? null);
	}

	private hasCameraPublicationForIdentity(participantIdentity: string): boolean {
		return this.nativeController != null || this.findCameraPublicationForIdentity(participantIdentity) != null;
	}

	private applyQuality(publication: RemoteTrackPublication, quality: VideoQualityLevel): void {
		try {
			const lkQuality = qualityMap[quality];
			publication.setVideoQuality(lkQuality);
			logger.debug('Quality set', {quality, lkQuality});
		} catch (error) {
			logger.error('Failed to set quality', {quality, error});
		}
	}

	private createObserver(participantIdentity: string, element: HTMLElement): IntersectionObserver {
		const observer = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				const isIntersecting = entry.isIntersecting;
				if (!this.snapshot.context.entries.has(participantIdentity)) continue;
				this.transition({
					type: 'subscription.intersection',
					participantIdentity,
					hasPublication: this.findCameraPublicationForIdentity(participantIdentity) != null,
					isIntersecting,
				});
				logger.debug('Intersection changed', {participantIdentity, isIntersecting});
			}
		}, this.intersectionOptions);
		observer.observe(element);
		return observer;
	}

	private transition(event: VoiceSubscriptionEvent): void {
		runInAction(() => {
			this.snapshot = transitionVoiceSubscriptionSnapshot(this.snapshot, event);
		});
		const commands = [...this.snapshot.context.commands];
		runInAction(() => {
			this.snapshot = transitionVoiceSubscriptionSnapshot(this.snapshot, {type: 'subscription.clearCommands'});
		});
		for (const command of commands) {
			this.applyCommand(command);
		}
	}

	private applyCommand(command: VoiceSubscriptionCommand): void {
		switch (command.type) {
			case 'subscribePublication':
				this.subscribePublication(command.participantIdentity, command.enabled, command.quality);
				break;
			case 'unsubscribePublication':
				this.unsubscribePublication(command.participantIdentity);
				break;
			case 'setPublicationEnabled':
				this.setPublicationEnabled(command.participantIdentity, command.enabled);
				break;
			case 'setPublicationQuality':
				this.setPublicationQuality(command.participantIdentity, command.quality);
				break;
			case 'observeElement':
				this.observers.set(
					command.participantIdentity,
					this.createObserver(command.participantIdentity, command.element as HTMLElement),
				);
				break;
			case 'disconnectObserver':
				this.observers.get(command.participantIdentity)?.disconnect();
				this.observers.delete(command.participantIdentity);
				break;
		}
	}

	private subscribePublication(participantIdentity: string, enabled: boolean, quality: VideoQualityLevel): void {
		if (this.nativeController) {
			this.nativeController.setRemoteTrackSubscription({
				participantIdentity,
				source: VoiceTrackSource.Camera,
				subscribed: true,
				enabled,
				quality,
			});
			return;
		}
		const publication = this.findCameraPublicationForIdentity(participantIdentity);
		if (!publication) {
			this.transition({type: 'subscription.publicationMissing', participantIdentity});
			return;
		}
		try {
			publication.setSubscribed(true);
			publication.setEnabled(enabled);
			this.applyQuality(publication, quality);
			logger.debug('Video subscribed successfully', {participantIdentity});
		} catch (error) {
			logger.error('Failed to subscribe', {participantIdentity, error});
		}
	}

	private unsubscribePublication(participantIdentity: string): void {
		if (this.nativeController) {
			this.nativeController.setRemoteTrackSubscription({
				participantIdentity,
				source: VoiceTrackSource.Camera,
				subscribed: false,
				enabled: false,
			});
			return;
		}
		const publication = this.findCameraPublicationForIdentity(participantIdentity);
		if (!publication) return;
		try {
			publication.setSubscribed(false);
			logger.debug('Track unsubscribed', {participantIdentity});
		} catch (error) {
			logger.error('Failed to unsubscribe', {participantIdentity, error});
		}
	}

	private setPublicationEnabled(participantIdentity: string, enabled: boolean): void {
		if (this.nativeController) {
			this.nativeController.setRemoteTrackSubscription({
				participantIdentity,
				source: VoiceTrackSource.Camera,
				subscribed: true,
				enabled,
			});
			return;
		}
		const publication = this.findCameraPublicationForIdentity(participantIdentity);
		if (!publication) {
			this.transition({type: 'subscription.publicationMissing', participantIdentity});
			return;
		}
		try {
			publication.setEnabled(enabled);
			logger.debug('Track enabled state updated', {participantIdentity, enabled});
		} catch (error) {
			logger.error('Failed to set enabled state', {participantIdentity, enabled, error});
		}
	}

	private setPublicationQuality(participantIdentity: string, quality: VideoQualityLevel): void {
		if (this.nativeController) {
			this.nativeController.setRemoteTrackSubscription({
				participantIdentity,
				source: VoiceTrackSource.Camera,
				subscribed: true,
				quality,
			});
			return;
		}
		const publication = this.findCameraPublicationForIdentity(participantIdentity);
		if (!publication) {
			this.transition({type: 'subscription.publicationMissing', participantIdentity});
			return;
		}
		this.applyQuality(publication, quality);
		logger.debug('Quality updated', {participantIdentity, quality});
	}
}
